import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { suppliersApi } from './service';
import type {
  CreateSupplierPayload,
  SupplierListFilters,
  UpdateSupplierPayload,
} from './types';

/**
 * Query key factory.
 *
 *   suppliersKeys.lists()                every list (any filters)
 *   suppliersKeys.list(filters)          a specific filter combination
 *   suppliersKeys.detail(id)             a single supplier
 */
export const suppliersKeys = {
  all: ['suppliers'] as const,
  lists: () => [...suppliersKeys.all, 'list'] as const,
  list: (filters: SupplierListFilters) =>
    [...suppliersKeys.lists(), filters] as const,
  detail: (id: string) => [...suppliersKeys.all, 'detail', id] as const,
};

export function useSuppliers(filters: SupplierListFilters = {}) {
  return useQuery({
    queryKey: suppliersKeys.list(filters),
    queryFn: () => suppliersApi.list(filters),
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: id ? suppliersKeys.detail(id) : suppliersKeys.all,
    queryFn: () => suppliersApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSupplierPayload) => suppliersApi.create(input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: suppliersKeys.lists() });
      qc.setQueryData(suppliersKeys.detail(created.id), created);
    },
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateSupplierPayload) =>
      suppliersApi.update(id, patch),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: suppliersKeys.lists() });
      qc.setQueryData(suppliersKeys.detail(id), updated);
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => suppliersApi.remove(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: suppliersKeys.lists() });
      qc.removeQueries({ queryKey: suppliersKeys.detail(id) });
    },
  });
}

export function useAddSupplierScope(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      suppliersApi.addScope(supplierId, companyId),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: suppliersKeys.lists() });
      qc.setQueryData(suppliersKeys.detail(supplierId), updated);
    },
  });
}

export function useRemoveSupplierScope(supplierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      suppliersApi.removeScope(supplierId, companyId),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: suppliersKeys.lists() });
      qc.setQueryData(suppliersKeys.detail(supplierId), updated);
    },
  });
}
